import { useState, useEffect } from "react";
import { Plus, Trash2 } from "lucide-react";
import {
  FiEdit2,
  FiEye,
  FiFilter,
  FiPlus,
  FiPlusCircle,
  FiRotateCcw,
  FiSearch,
} from "react-icons/fi";
import { useCorporateInteriorDesign } from "../useHook";
import Swal from "sweetalert2";
import { useNavigate } from "react-router-dom";
import GlobalPagination from "../../../Components/Pagination/GlobalPagination";
import CorporateInteriorDesignModal from "../../../Components/Modals/CorporateInteriorDesign/CorporateInteriorDesignModal.jsx";
import AddPopularCorporateInteriorDesign from "../../../Components/Modals/PopularCorporateInteriorDesign/PopularCorporateInteriorDesign";
import SimilarItemsModal from "../../../Components/Modals/SimilarItems/SimilarItems";
import RecordPosition from "../../../Components/Modals/RecordPosition/RecordPosition";
import Loader from "../../../Utils/loader";
import { useCorporateInteriorDesignCategory } from "../useHook";
import { MdDelete } from "react-icons/md";
import { IoEye } from "react-icons/io5";
import {
  usePermission,
  SystemPermissions,
} from "../../../Utils/permissionAccess";

const CorporateInteriorDesign = () => {
  const navigate = useNavigate();
  const { hasPermission } = usePermission();

  const canCreate = hasPermission(SystemPermissions.CorporateInteriorDesign.CREATE);
  const canAddPopular = hasPermission(SystemPermissions.CorporateInteriorDesign.ADD_POPULAR_DESIGN);
  const canPosition = hasPermission(SystemPermissions.CorporateInteriorDesign.POSITION);
  const canAddSimilar = hasPermission(SystemPermissions.CorporateInteriorDesign.ADD_SIMILAR);
  const canView = hasPermission(SystemPermissions.CorporateInteriorDesign.VIEW);
  const canUpdate = hasPermission(SystemPermissions.CorporateInteriorDesign.UPDATE);
  const canDelete = hasPermission(SystemPermissions.CorporateInteriorDesign.DELETE);
  const canShowActions = canAddSimilar || canUpdate || canView || canDelete;
  const {
    getCorporateInteriorDesign,
    createCorporateInteriorDesign,
    updateCorporateInteriorDesign,
    deleteCorporateInteriorDesign,
    getCorporateInteriorDesignById,
    updateCorporateInteriorDesignPosition,
  } = useCorporateInteriorDesign();
  const { getAllCorporateInteriorDesignCategories } = useCorporateInteriorDesignCategory();

  const [showModal, setShowModal] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [editId, setEditId] = useState(null);
  const [editData, setEditData] = useState(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [corporateInteriorDesignData, setCorporateInteriorDesignData] = useState([]);
  const [offset, setOffset] = useState(0);
  const [limit] = useState(10);
  const [total, setTotal] = useState(0);
  const [isLoading, setIsLoading] = useState(false);
  const [showPopularDesignModal, setShowPopularDesignModal] = useState(false);
  const [isSimilarModalOpen, setIsSimilarModalOpen] = useState(false);
  const [selectedDesignForSimilar, setSelectedDesignForSimilar] = useState(null);
  const [searchFilter, setSearchFilter] = useState("");
  const [categoryName, setCategoryName] = useState("");
  const [isPositionModalOpen, setIsPositionModalOpen] = useState(false);
  const [selectedDesignForPosition, setSelectedDesignForPosition] =
    useState(null);
  const [corporateInteriorDesignCategories, setCorporateInteriorDesignCategories] = useState([]);

  useEffect(() => {
    const fetchCategories = async () => {
      setIsLoading(true);
      try {
        const categories = await getAllCorporateInteriorDesignCategories();
        setCorporateInteriorDesignCategories(categories || []);
        const defaultCategoryName =
          categories?.length > 0 ? categories[0].name : "";
        setCategoryName(defaultCategoryName);
        await getCorporateInteriorDesign(
          setCorporateInteriorDesignData,
          setTotal,
          0,
          limit,
          searchFilter,
          defaultCategoryName,
        );
      } finally {
        setIsLoading(false);
      }
    };
    fetchCategories();
  }, []);
  const handlePageChange = (newOffset) => {
    setOffset(newOffset);
    loadCorporateInteriorDesigns(newOffset, limit);
  };

  const loadCorporateInteriorDesigns = async (
    currentOffset = offset,
    currentLimit = limit,
  ) => {
    setIsLoading(true);
    try {
      await getCorporateInteriorDesign(
        setCorporateInteriorDesignData,
        setTotal,
        currentOffset,
        currentLimit,
        searchFilter,
        categoryName,
      );
    } catch (error) {
      Swal.fire({
        icon: "error",
        title: "Error",
        text: "Failed to load corporate interior designs. Please refresh the page.",
      });
    } finally {
      setIsLoading(false);
    }
  };

  const handleSubmit = async (formData) => {
    setIsSubmitting(true);

    try {
      const apiData = new FormData();

      Object.keys(formData).forEach((key) => {
        if (
          key !== "images" &&
          key !== "imagePreviews" &&
          key !== "primaryImageIndex" &&
          key !== "imagePositions" &&
          key !== "imageAlts" &&
          key !== "imageTitles" &&
          key !== "existingImages" &&
          key !== "existingImagePositions" &&
          key !== "primaryExistingImageId"
        ) {
          if (key === "features") {
            apiData.append(key, JSON.stringify(formData[key]));
          } else {
            apiData.append(key, formData[key]);
          }
        }
      });

      if (formData.primaryImageIndex !== null && formData.primaryImageIndex !== undefined) {
        apiData.append("primaryImageIndex", formData.primaryImageIndex);
      }
      
      if (formData.imagePositions && formData.imagePositions.length > 0) {
        formData.imagePositions.forEach((pos) => {
          apiData.append("imagePositions", pos);
        });
      }

      if (formData.images && formData.images.length > 0) {
        formData.images.forEach((image, index) => {
          apiData.append(`image`, image);
          const alt = formData.imageAlts?.[index];
          const title = formData.imageTitles?.[index];
          if (alt != null && String(alt).trim()) {
            apiData.append("imageAlt", String(alt).trim());
          }
          if (title != null && String(title).trim()) {
            apiData.append("imageTitle", String(title).trim());
          }
        });
      }

      if (isEditing) {
        if (formData.existingImages) {
          const keepImageIds = formData.existingImages.map((img) => img.id);
          apiData.append("keepImageIds", JSON.stringify(keepImageIds));

          const existingImageAlts = {};
          const existingImageTitles = {};
          formData.existingImages.forEach((img) => {
            if (img.id != null) {
              if (img.image_alt != null && String(img.image_alt).trim()) {
                existingImageAlts[img.id] = String(img.image_alt).trim();
              }
              if (img.image_title != null && String(img.image_title).trim()) {
                existingImageTitles[img.id] = String(img.image_title).trim();
              }
            }
          });
          if (Object.keys(existingImageAlts).length > 0) {
            apiData.append("existingImageAlts", JSON.stringify(existingImageAlts));
          }
          if (Object.keys(existingImageTitles).length > 0) {
            apiData.append(
              "existingImageTitles",
              JSON.stringify(existingImageTitles),
            );
          }
        }

        if (formData.existingImagePositions) {
          apiData.append(
            "existingImagePositions",
            JSON.stringify(formData.existingImagePositions)
          );
        }

        if (formData.primaryExistingImageId !== null) {
          apiData.append("primaryExistingImageId", formData.primaryExistingImageId);
        }
      }

      let response;
      if (isEditing) {
        response = await updateCorporateInteriorDesign(editId, apiData);
      } else {
        response = await createCorporateInteriorDesign(apiData);
      }

      await loadCorporateInteriorDesigns(offset, limit);

      setShowModal(false);
      setIsEditing(false);
      setEditId(null);
      setEditData(null);

      Swal.fire({
        icon: "success",
        title: isEditing ? "Corporate Interior Design Updated" : "Corporate Interior Design Added",
        showConfirmButton: false,
        timer: 1500,
      });
    } catch (error) {
      console.error("Failed to submit form:", error);
      Swal.fire({
        icon: "error",
        title: "Error",
        text: "Failed to save corporate interior design. Please try again.",
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const handlePositionClick = (design) => {
    setSelectedDesignForPosition(design);
    setIsPositionModalOpen(true);
  };

  const handleUpdatePosition = async (corporateInteriorDesignId, newPosition) => {
    await updateCorporateInteriorDesignPosition(corporateInteriorDesignId, newPosition);
    await loadCorporateInteriorDesigns(offset, limit);
  };

  const closePositionModal = () => {
    setIsPositionModalOpen(false);
    setSelectedDesignForPosition(null);
  };

  const handleEdit = async (id) => {
    try {
      // Load full record (same endpoint as Express get-single-corporate-interior-design/:id)
      const design =
        (await getCorporateInteriorDesignById(id)) ||
        corporateInteriorDesignData.find((item) => item.id === id);
      if (!design) {
        throw new Error("Interior design not found");
      }
      const transformedDesign = {
        ...design,
        existingImages: design.images || [],
      };
      setEditData(transformedDesign);
      setIsEditing(true);
      setEditId(id);
      setShowModal(true);
    } catch (error) {
      console.error("Failed to load design for editing:", error);
      Swal.fire({
        icon: "error",
        title: "Error",
        text: "Failed to load design for editing. Please try again.",
      });
    }
  };

  const handleDelete = async (id) => {
    const result = await Swal.fire({
      title: "Are you sure?",
      text: "You won't be able to revert this!",
      icon: "warning",
      showCancelButton: true,
      confirmButtonText: "Yes, delete it!",
      cancelButtonText: "No, cancel!",
      reverseButtons: true,
    });

    if (result.isConfirmed) {
      try {
        await deleteCorporateInteriorDesign(id);
        await loadCorporateInteriorDesigns(offset, limit);
        Swal.fire(
          "Deleted!",
          "Interior design deleted successfully.",
          "success",
        );
      } catch (error) {
        console.error("Failed to delete corporate interior design:", error);
        Swal.fire(
          "Error!",
          "Failed to delete corporate interior design. Please try again.",
          "error",
        );
      }
    }
  };

  const handlePageView = (id) => {
    navigate(`/corporate-interior-design/${id}`);
  };

  const handleAddNew = () => {
    setIsEditing(false);
    setEditId(null);
    setEditData(null);
    setShowModal(true);
  };

  const handleAddSimilarDesign = (id) => {
    setSelectedDesignForSimilar(id);
    setIsSimilarModalOpen(true);
  };

  const handleSearch = async () => {
    setIsLoading(true);
    setOffset(0);
    try {
      await getCorporateInteriorDesign(
        setCorporateInteriorDesignData,
        setTotal,
        0,
        limit,
        searchFilter,
        categoryName,
      );
    } catch (error) {
      Swal.fire({
        icon: "error",
        title: "Error",
        text: "Failed to search corporate interior designs. Please try again.",
      });
    } finally {
      setIsLoading(false);
    }
  };

  const handleReset = () => {
    setSearchFilter("");
    const defaultCategoryName =
      corporateInteriorDesignCategories?.length > 0
        ? corporateInteriorDesignCategories[0].name
        : "";
    setCategoryName(defaultCategoryName);
    setOffset(0);
    getCorporateInteriorDesign(
      setCorporateInteriorDesignData,
      setTotal,
      0,
      limit,
      "",
      defaultCategoryName,
    );
  };

  if (isLoading) {
    return <Loader message="Loading corporate interior designs..." />;
  }

  return (
    <div>
      <div className="flex flex-wrap gap-4 justify-between items-center">
        <h3 className="text-xl font-bold">Corporate Interior Design</h3>

        <div className="flex flex-wrap gap-2">
          {canCreate && (
            <button onClick={handleAddNew} className="btn btn-primary h-[44px]">
              <FiPlus className="text-white" />
              Add Corporate Interior Design
            </button>
          )}

          {canAddPopular && (
            <button
              onClick={() => setShowPopularDesignModal(true)}
              className="btn btn-primary h-[44px]"
            >
              <FiPlus className="text-white" />
              Add Popular Design
            </button>
          )}
        </div>
      </div>

      <div className="bg-white p-5 w-full rounded-lg pagination-shadow flex flex-wrap xlCustom:flex-nowrap md:justify-end items-center gap-3 my-5">
        <div className="search-wrapper">
          <input
            type="text"
            className="search-input"
            placeholder="Search by name"
            value={searchFilter}
            onChange={(e) => setSearchFilter(e.target.value)}
          />
          <FiSearch className="search-icon" />
        </div>
        <select
          className="status-select"
          value={categoryName}
          onChange={(e) => setCategoryName(e.target.value)}
        >
          {corporateInteriorDesignCategories.length === 0 ? (
            <option value="">Loading...</option>
          ) : (
            <option value="">All categories</option>
          )}
          {corporateInteriorDesignCategories.map((cat) => (
            <option key={cat.id} value={cat.name}>
              {cat.name}
            </option>
          ))}
        </select>
        <div className="flex flex-wrap md:flex-nowrap gap-2">
          <button className="btn btn-filter filter-btn" onClick={handleSearch}>
            <FiFilter className="text-white" /> Apply Filter
          </button>
          <button
            className="btn btn-reset-filter filter-btn"
            onClick={handleReset}
          >
            <FiRotateCcw className="text-white" /> Reset Filter
          </button>
        </div>
      </div>

      {/* Table */}
      <div className="bg-white w-full rounded-lg pagination-shadow">
        <div className="overflow-x-auto">
          <table className="min-w-full table-auto bg-white rounded-lg  overflow-hidden">
            <thead>
              <tr className="brand-gradient text-white">
                <th className="table-th">Sr. No.</th>
                <th className="table-th">Title</th>
                <th className="table-th">Room Size</th>
                <th className="table-th">Category</th>
                {/* <th className="py-3 px-4 text-left">Design Type</th> */}
                {canShowActions && <th className="table-th">Actions</th>}
              </tr>
            </thead>
            <tbody>
              {corporateInteriorDesignData.length > 0 ? (
                corporateInteriorDesignData.map((design, index) => (
                  <tr
                    key={design.id}
                    className={`${
                      index % 2 === 0 ? "bg-gray-50" : "bg-gray-100"
                    } hover:bg-gray-200 transition-colors duration-300`}
                  >
                    <td className="table-td">
                      {canPosition ? (
                        <button
                          type="button"
                          onClick={() => handlePositionClick(design)}
                          className="text-blue-600 hover:text-blue-800 hover:underline cursor-pointer font-medium"
                        >
                          {design.position ?? offset + index + 1}
                        </button>
                      ) : (
                        <span className="font-medium text-gray-800">
                          {design.position ?? offset + index + 1}
                        </span>
                      )}
                    </td>
                    <td className="table-td">{design.title}</td>
                    <td className="table-td">{design.room_size}</td>
                    <td className="table-td">{design.category?.name}</td>
                    {/* <td className="py-3 px-4">{design.design_type}</td> */}
                    {canShowActions && (
                      <td className="table-td flex space-x-2">
                        {canAddSimilar && (
                          <button
                            className="btn btn-action"
                            onClick={() => handleAddSimilarDesign(design.id)}
                          >
                            <FiPlusCircle className="text-white" />
                            Similar Items
                          </button>
                        )}
                        {canUpdate && (
                          <button
                            className="btn btn-edit text-white"
                            onClick={() => handleEdit(design.id)}
                          >
                            <FiEdit2 className="text-white" /> Edit
                          </button>
                        )}
                        {canView && (
                          <button
                            className="btn btn-view "
                            onClick={() => handlePageView(design.id)}
                          >
                            <IoEye className="text-white" /> View
                          </button>
                        )}
                        {canDelete && (
                          <button
                            className="btn btn-delete text-white"
                            onClick={() => handleDelete(design.id)}
                          >
                            <MdDelete className="text-white" /> Delete
                          </button>
                        )}
                      </td>
                    )}
                  </tr>
                ))
              ) : (
                <tr>
                  <td
                    colSpan={canShowActions ? 5 : 4}
                    className="py-4 px-4 text-center text-gray-500 font-bold"
                  >
                    No corporate interior designs found
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* Pagination */}
      {corporateInteriorDesignData.length > 0 && total > limit && (
        <div className="mt-4">
          <GlobalPagination
            offset={offset}
            limit={limit}
            total={total}
            onPageChange={handlePageChange}
            className="bg-white"
          />
        </div>
      )}

      {/* Corporate Interior Design Modal */}
      <CorporateInteriorDesignModal
        isOpen={showModal}
        onClose={() => {
          setShowModal(false);
          setIsEditing(false);
          setEditId(null);
          setEditData(null);
        }}
        onSubmit={handleSubmit}
        isEditing={isEditing}
        editData={editData}
        isSubmitting={isSubmitting}
      />

      {/* Add Popular Corporate Interior Design Modal */}
      {showPopularDesignModal && (
        <AddPopularCorporateInteriorDesign
          isOpen={showPopularDesignModal}
          onClose={() => setShowPopularDesignModal(false)}
        />
      )}

      {isSimilarModalOpen && (
        <SimilarItemsModal
          isOpen={isSimilarModalOpen}
          onClose={() => {
            setIsSimilarModalOpen(false);
            setSelectedDesignForSimilar(null);
          }}
          sourceId={selectedDesignForSimilar}
          sourceType="corporate_interior_design"
        />
      )}

      {isPositionModalOpen && selectedDesignForPosition && (
        <RecordPosition
          isOpen={isPositionModalOpen}
          onClose={closePositionModal}
          currentPosition={selectedDesignForPosition.position ?? 0}
          recordId={selectedDesignForPosition.id}
          onUpdatePosition={handleUpdatePosition}
          moduleName="Corporate Interior Design"
        />
      )}
    </div>
  );
};

export default CorporateInteriorDesign;
